import { useEffect, useId } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from 'components/Dialog';
import { Box, Button } from '@mui/material';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { closeModal } from 'features/modal/modalSlice';
import {
  selectBreakoutRoomsCreated,
  selectBreakoutRoomsStatus,
  selectBreakoutRoomTimer,
} from 'features/breakout-rooms/breakoutRoomsSlice';
import { useBreakoutRooms } from 'features/breakout-rooms/useBreakoutRooms';
import { dayjsToSeconds, secondsToDayjs } from 'utils/date';
import { editBreakoutRoomsRequested } from 'features/breakout-rooms/actions';
import { EditBreakoutRoomsPayload } from 'features/breakout-rooms/types';
import BreakoutTimerFields, {
  BreakoutTimerFieldsValues,
} from 'features/breakout-rooms/components/BreakoutTimerFields';

const EditBreakoutRoomsSettingsModal = () => {
  const { t } = useTranslation(['modals', 'breakout-rooms']);
  const dispatch = useAppDispatch();
  const modalId = useId();

  const breakoutTimer = useAppSelector(selectBreakoutRoomTimer);
  const breakoutRoomsCreated = useAppSelector(selectBreakoutRoomsCreated);
  const status = useAppSelector(selectBreakoutRoomsStatus);
  const { hasBreakoutManagementAccess } = useBreakoutRooms();

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm<BreakoutTimerFieldsValues>({
    mode: 'onChange',
    defaultValues: {
      timer: true,
      totalSeconds: breakoutTimer ? secondsToDayjs(breakoutTimer.totalSeconds) : undefined,
      stopBreakouts: breakoutTimer?.stopBreakouts || false,
    },
  });

  const modalShouldBeClosed =
    !hasBreakoutManagementAccess || !breakoutRoomsCreated || status === 'started';

  useEffect(() => {
    if (modalShouldBeClosed) {
      dispatch(closeModal('editBreakoutRoomsSettings'));
    }
  }, [modalShouldBeClosed, dispatch]);

  const saveSettings: SubmitHandler<BreakoutTimerFieldsValues> = (data) => {
    const payload: EditBreakoutRoomsPayload = {};
    if (data.timer && data.totalSeconds) {
      payload.timer = {
        totalSeconds: dayjsToSeconds(data.totalSeconds),
        stopBreakouts: data.stopBreakouts,
      };
    }

    dispatch(editBreakoutRoomsRequested(payload));
    dispatch(closeModal('editBreakoutRoomsSettings'));
  };

  const handleCancel = () => {
    dispatch(closeModal('editBreakoutRoomsSettings'));
  };

  return (
    <Dialog
      open={!modalShouldBeClosed}
      maxWidth="sm"
      aria-labelledby={`${modalId}-title`}
      onClose={handleCancel}
    >
      <DialogTitle id={`${modalId}-title`} onClose={handleCancel}>
        {t('modals:breakout_rooms.edit_rooms_settings.title_timer')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <BreakoutTimerFields
            control={control}
            errors={errors}
            labels={{
              enable: t('modals:breakout_rooms.edit_rooms_settings.enable_label'),
              duration: t('modals:breakout_rooms.edit_rooms_settings.duration_label'),
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button type="button" color="grayscale" onClick={handleCancel}>
          {t('modals:breakout_rooms.edit_rooms_settings.btn_cancel_label')}
        </Button>
        <Button type="button" onClick={handleSubmit(saveSettings)} disabled={!isValid}>
          {t('modals:breakout_rooms.edit_rooms_settings.btn_save_label')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBreakoutRoomsSettingsModal;
